import React, { useMemo, useState } from "react";
import UIkit from "uikit";
import InventoryAddPhotosOffcanvas from "./add";
/**
 * Home Inventory (Photos)
 * - Column 1: Properties list + search
 * - Column 2: Photo gallery for selected property
 * - Bottom: Add photos (file upload + URL)
 *
 * Notes:
 * - This is a front-end only demo with local state.
 * - Wire to your API by replacing the TODO sections in handlers.
 */

const safeArray = (v) => (Array.isArray(v) ? v : []);

const titleCase = (s) =>
  String(s || "")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const labelForProperty = (p) => titleCase(p?.name || p?.title || p?.nickname || p?.pms_id || p?.id);

const entityKey = (p) => String(p?.rental_id || p?.property_id || p?.pms_id || p?.id || "");

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return String(iso);
  }
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const DEMO = {
  properties: [
    {
      id: "p1",
      pms_id: "RB-101",
      name: "Oceanview Cottage",
      city: "Cannon Beach",
      state: "OR",
      photos: [
        {
          id: "ph1",
          url: "/images/home_inventory/oceanview/image-1.png",
          label: "Coffeemaker",
          category: "Appliances",
          condition: "Good",
          quantity: 1,
          uploadedAt: "2025-12-18T15:42:00",
        },
        {
          id: "ph2",
          url: "/images/home_inventory/oceanview/image-2.png",
          label: "Toaster",
          category: "Appliances",
          condition: "Good",
          quantity: 1,
          uploadedAt: "2025-12-18T15:45:00",
        },
        {
          id: "ph3",
          url: "/images/home_inventory/oceanview/image-3.png",
          label: "Grill",
          category: "Outdoor",
          condition: "Good",
          quantity: 1,
          uploadedAt: "2025-12-18T15:51:00",
        },
        {
          id: "ph4",
          url: "/images/home_inventory/oceanview/image-4.png",
          label: "Kitchen",
          category: "Rooms",
          condition: "Good",
          quantity: 1,
          uploadedAt: "2025-12-18T16:15:00",
        },
      ],
    },
    {
      id: "p2",
      pms_id: "RB-102",
      name: "Downtown Loft",
      city: "Portland",
      state: "OR",
      photos: [
        {
          id: "ph5",
          url: "/images/home_inventory/loft/image-1.png",
          label: "Coffeemaker",
          category: "Appliances",
          condition: "Good",
          quantity: 1,
          uploadedAt: "2025-12-19T10:29:00",
        },
        {
          id: "ph6",
          url: "/images/home_inventory/loft/image-2.png",
          label: "Grill",
          category: "Outdoor",
          condition: "Good",
          quantity: 1,
          uploadedAt: "2025-12-19T10:29:00",
        },
      ],
    },
    {
      id: "p3",
      pms_id: "RB-103",
      name: "Mountain Cabin",
      city: "Bend",
      state: "OR",
      photos: [
        {
          id: "ph7",
          url: "/images/home_inventory/cabin/image-1.png",
          label: "Coffeemaker",
          category: "Appliances",
          condition: "Good",
          quantity: 1,
          uploadedAt: "2025-12-17T10:29:00",
        },
        {
          id: "ph8",
          url: "/images/home_inventory/cabin/image-2.png",
          label: "Vacuum Cleaner",
          category: "Cleaning",
          condition: "Good",
          quantity: 1,
          uploadedAt: "2025-12-17T10:25:00",
        },
        {
          id: "ph9",
          url: "/images/home_inventory/cabin/image-3.png",
          label: "Kitchen",
          category: "Rooms",
          condition: "Good",
          quantity: 1,
          uploadedAt: "2025-12-17T10:23:00",
        },
      ],
    },
  ],
};

export default function HomeInventory() {
  const [properties, setProperties] = useState(DEMO.properties);
  const [selectedId, setSelectedId] = useState(entityKey(DEMO.properties[0]));
  const [q, setQ] = useState("");

  // Add photos form
  const [urlToAdd, setUrlToAdd] = useState("");
  const [labelToAdd, setLabelToAdd] = useState("");
  const [filesToAdd, setFilesToAdd] = useState([]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return properties;

    return properties.filter((p) => {
      const hay = [p?.name, p?.pms_id, p?.city, p?.state, p?.id].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(query);
    });
  }, [properties, q]);

  const selectedProperty = useMemo(() => {
    return properties.find((p) => entityKey(p) === String(selectedId)) || null;
  }, [properties, selectedId]);

  const onOpenPhoto = (photo) => {
    UIkit.modal.dialog(`
      <div class="uk-modal-body">
        <h3 class="uk-modal-title">${photo?.label ? photo.label : "Photo"}</h3>
        <img src="${photo.url}" style="width:100%;border-radius:12px;" />
      </div>
    `);
  };

  const addPhotosToSelectedProperty = (newPhotos) => {
    if (!selectedProperty) return;

    setProperties((prev) =>
      prev.map((p) => {
        if (entityKey(p) !== entityKey(selectedProperty)) return p;
        return { ...p, photos: [...safeArray(newPhotos), ...safeArray(p.photos)] };
      })
    );
  };
  const onRemovePhoto = (photoId) => {
    if (!selectedProperty) return;

    setProperties((prev) =>
      prev.map((p) => {
        if (entityKey(p) !== entityKey(selectedProperty)) return p;
        return { ...p, photos: safeArray(p.photos).filter((ph) => ph.id !== photoId) };
      })
    );

    UIkit.notification({ message: "Photo removed.", status: "success" });
  };

  const onAddUrlPhoto = () => {
    if (!selectedProperty) return;
    const url = urlToAdd.trim();
    if (!url) {
      UIkit.notification({ message: "Paste a photo URL first.", status: "warning" });
      return;
    }

    const newPhoto = {
      id: `ph_${Date.now()}`,
      url,
      label: labelToAdd.trim(),
      category: "Uncategorized",
      condition: "Good",
      quantity: 1,
      uploadedAt: new Date().toISOString().slice(0, 10),
    };

    setProperties((prev) =>
      prev.map((p) => {
        if (entityKey(p) !== entityKey(selectedProperty)) return p;
        return { ...p, photos: [newPhoto, ...safeArray(p.photos)] };
      })
    );

    setUrlToAdd("");
    setLabelToAdd("");
    UIkit.notification({ message: "Photo added.", status: "success" });

    // TODO: POST to API
    // await api.post(`/api/inventory/photos`, { property_id: selectedProperty.id, url, label })
  };

  const onAddFilePhotos = async () => {
    if (!selectedProperty) return;

    if (!filesToAdd.length) {
      UIkit.notification({ message: "Choose one or more files first.", status: "warning" });
      return;
    }

    // Demo: create local previews. In production: upload to server -> get URLs back.
    const previews = Array.from(filesToAdd).map((f) => ({
      id: `ph_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      url: URL.createObjectURL(f),
      label: f.name,
      category: "Uncategorized",
      condition: "Good",
      quantity: 1,
      uploadedAt: new Date().toISOString().slice(0, 10),
      _local: true,
    }));

    setProperties((prev) =>
      prev.map((p) => {
        if (entityKey(p) !== entityKey(selectedProperty)) return p;
        return { ...p, photos: [...previews, ...safeArray(p.photos)] };
      })
    );

    setFilesToAdd([]);
    UIkit.notification({ message: "Photos added (local preview).", status: "success" });

    // TODO: real upload
    // const fd = new FormData()
    // filesToAdd.forEach(f => fd.append("files", f))
    // fd.append("property_id", selectedProperty.id)
    // const res = await api.post(`/api/inventory/photos/upload`, fd)
    // res.data.urls -> merge into property photos list
  };

  return (
    <section>
      <div id="hms-page-title" className="uk-flex uk-flex-between uk-flex-middle">
        <div>
          <h1 className="uk-heading-small">Home Inventory</h1>
          <div className="uk-text-meta">Track photos of important items so nothing is broken or missing.</div>
        </div>
      </div>

      <div className="uk-grid-small" uk-grid="true">
        {/* Column 1: Properties */}
        <div className="uk-width-1-4@l uk-width-1-3@m uk-width-1-1">
          <div className="uk-card uk-card-default uk-card-small">
            <div className="uk-card-header uk-padding-small">
              <div className="uk-text-bold">Properties</div>
              <div className="uk-margin-small-top">
                <input
                  className="uk-input uk-form-small"
                  placeholder="Search…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
            </div>

            <div className="uk-card-body uk-padding-small">
              {filtered.length === 0 ? (
                <div className="uk-text-meta uk-padding-small">No properties found.</div>
              ) : (
                <ul className="uk-list uk-list-divider">
                  {filtered.map((p) => {
                    const pk = entityKey(p);
                    const active = String(pk) === String(selectedId);
                    const count = safeArray(p.photos).length;

                    return (
                      <li key={pk}>
                        <button
                          type="button"
                          onClick={() => setSelectedId(pk)}
                          className={`uk-button uk-button-text uk-text-left ${active ? "hms-selected-row" : ""}`}
                          style={{ width: "100%", padding: "8px 6px 8px 12px", borderRadius: 6 }}
                        >
                          <div className="uk-flex uk-flex-between uk-flex-middle">
                            <div>
                              <div className="uk-text-bold">{labelForProperty(p)}</div>
                              <div className="uk-text-meta">
                                {p?.pms_id ? `ID: ${p.pms_id}` : ""}
                                {p?.pms_id && (p?.city || p?.state) ? " • " : ""}
                                {[p?.city, p?.state].filter(Boolean).join(", ")}
                              </div>
                            </div>
                            <span className="uk-badge">{count}</span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Column 2: Gallery */}
        <div className="uk-width-expand">
          <div className="uk-card uk-card-default uk-card-small">
            <div className="uk-card-header uk-padding-small uk-flex uk-flex-between uk-flex-middle">
              <div>
                <div className="uk-text-bold">Photos</div>
                <div className="uk-text-meta">
                  {selectedProperty ? `${labelForProperty(selectedProperty)} — ${safeArray(selectedProperty.photos).length} photos` : "Select a property"}
                </div>
              </div>
            </div>

            <div className="uk-card-body uk-padding-small">
              {!selectedProperty ? (
                <div className="uk-text-meta uk-padding-small">Pick a property to view photos.</div>
              ) : safeArray(selectedProperty.photos).length === 0 ? (
                <div className="uk-text-meta uk-padding-small">No photos yet. Add some below.</div>
              ) : (
                <div className="uk-child-width-1-3@s uk-child-width-1-4@m uk-grid-small" uk-grid="true">
                  {safeArray(selectedProperty.photos).map((ph) => (
                    <div key={ph.id}>
                      <div className="uk-card uk-card-default uk-card-small uk-border-rounded uk-margin-right">
                        <div className="uk-card-media-top" style={{ position: "relative" }}>
                          <img
                            src={ph.url}
                            alt={ph.label || "Inventory photo"}
                            style={{ width: "100%", height: 140, objectFit: "cover", borderTopLeftRadius: 12, borderTopRightRadius: 12, cursor: "pointer" }}
                            onClick={() => onOpenPhoto(ph)}
                          />

                          <button
                            type="button"
                            className="uk-button uk-button-small uk-button-default"
                            style={{ position: "absolute", top: 8, right: 8 }}
                            onClick={() => onRemovePhoto(ph.id)}
                            title="Remove"
                          >
                            ✕
                          </button>
                        </div>

                        <div className="uk-card-body uk-padding-small">
                          <div className="uk-text-bold">{ph.label || "Untitled"}</div>

                          <div className="uk-text-meta uk-text-small">
                            {ph.category ? <span className="uk-label uk-label-default">{ph.category}</span> : null}
                            {ph.condition ? <span className="uk-label uk-label-default">{ph.condition}</span> : null}
                          </div>

                          <div className="uk-text-meta uk-text-small">
                            {ph.quantity != null ? <span>Qty: {ph.quantity}</span> : null}
                          </div>

                          <div className="uk-text-meta uk-text-small">
                            {ph.uploadedAt ? <span>Uploaded: {formatDateTime(ph.uploadedAt)}</span> : null}
                          </div>

                          <div className="uk-text-meta uk-text-small">
                            {ph._local ? "Local preview" : "Saved"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <hr className="uk-margin" />

              <div className="uk-card-header uk-padding-small uk-flex uk-flex-between uk-flex-middle">
                <div>
                  <div className="uk-text-bold">Photos</div>
                  <div className="uk-text-meta">
                    {selectedProperty
                      ? `${labelForProperty(selectedProperty)} — ${safeArray(selectedProperty.photos).length} photos`
                      : "Select a property"}
                  </div>
                </div>

                <button
                  type="button"
                  className="uk-button uk-button-small hms-btn hms-red-btn"
                  onClick={() => UIkit.offcanvas("#inventory-add-photos")?.show()}
                  disabled={!selectedProperty}
                >
                  + Add Photos
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <InventoryAddPhotosOffcanvas
        refId="inventory-add-photos"
        disabled={!selectedProperty}
        propertyLabel={selectedProperty ? labelForProperty(selectedProperty) : ""}
        onSave={addPhotosToSelectedProperty}
      />
    </section>
  );
}